<template>
  <div class="am3-input-container am3-input-select-container">
    <AM3InputLabel
      :label="label"
      :required="required"
      :input-id="inputId"
    />
    <!-- Expanded-with-tags box math: AM3Tag is h-6 (24px) with my-1 (4px top+bottom),
         so each row's margin-box is 32px -- matching the input's own h-8 once tags
         exist. py-1.5 (6px top+bottom) frames that as (44 - 32) / 2 = 6px either
         side, landing exactly on the 44px base for one row; since padding is a fixed
         top/bottom amount regardless of content, N rows get that same 6/6 framing.
         The 10-row cap adds that same 12px padding to the pure content-stack height
         (10 * 32 + 9 * gap-1 (4px) = 356px), giving 368px before it scrolls instead
         of growing the input indefinitely. -->
    <div
      :id="inputId"
      class="relative flex flex-row text-sm border rounded-md am3-input-wrapper bg-surface-light hover:border-brand border-surface-dark outline-1 outline-offset-0 outline-transparent outline focus-within:outline-brand group"
      :class="[
        disabled ? 'disabled cursor-not-allowed' : '',
        isOpen ? 'focused outline-brand' : '',
        validationState === false || typeof validationState === 'string'
          ? 'error border-[var(--font-colour-warning)]'
          : '',
        !expands || selectedOptions.length === 0
          ? 'h-11'
          : 'items-center py-1.5 max-h-[368px] overflow-y-auto flex-wrap gap-1 pl-2 pr-20',
      ]"
      :data-test-id="generateDataTestId(dataTestId, 'input-wrapper')"
      @click="toggleDropdown"
    >
      <!-- Expanded mode: tags render as direct flex items (not nested in their own
           wrapping div) so they interleave with the input in one continuous wrapping
           row -- the input lands right after the last tag instead of dropping to its
           own full-width line, mirroring MUI Autocomplete's multi-value layout. -->
      <template v-if="expands && multiple && selectedOptions.length > 0">
        <AM3Tag
          v-for="(option, index) in selectedOptions"
          :key="getOptionKey(option, index)"
          :closable="true"
          :label="getOptionLabel(option)"
          class="flex-shrink-0 my-1"
          @close="removeOption(option)"
        />
      </template>

      <!-- Compact mode: fixed-height row with a "+N" overflow badge -->
      <div
        v-else-if="multiple && selectedOptions.length > 0"
        ref="tagsContainerRef"
        class="flex items-center flex-shrink-0 gap-1 p-1 ml-2"
      >
        <!-- Show as many tags as possible while keeping input at 100px -->
        <AM3Tag
          v-for="(option, index) in visibleTagsInNormalMode"
          :key="getOptionKey(option, index)"
          :closable="true"
          :label="getOptionLabel(option)"
          class="flex-shrink-0"
          @close="removeOption(option)"
        />
        <div
          v-if="selectedOptions.length > visibleTagsInNormalMode.length"
          class="am3-selected-tag am3-tag-more"
        >
          <span>+{{
            selectedOptions.length - visibleTagsInNormalMode.length
          }}</span>
        </div>
      </div>

      <!-- Left slot -->
      <div
        v-if="$slots.left"
        class="flex items-center am3-input-slot left"
      >
        <slot name="left" />
      </div>

      <!-- Input element -->
      <input
        :id="generateDataTestId(dataTestId, 'input')"
        ref="inputRef"
        :class="expandedInputSize
          ? 'outline-none text-ellipsis bg-transparent placeholder:text-secondary cursor-pointer h-8 rounded-md focus:ring-0 pl-3 pr-6 flex-none min-w-[24px]'
          : 'outline-none text-ellipsis bg-transparent placeholder:text-secondary cursor-pointer h-11 rounded-md focus:ring-0 pl-3 pr-6 flex-1 min-w-[100px]'"
        :size="expandedInputSize"
        :value="searchText"
        :placeholder="multiple && selectedOptions.length > 0 ? '' : placeholder"
        :disabled="disabled"
        :data-selected="multiple ? !!modelValue?.length : !!modelValue"
        :data-test-id="generateDataTestId(dataTestId, 'input')"
        role="combobox"
        :aria-expanded="isOpen"
        aria-haspopup="listbox"
        :aria-activedescendant="focusedIndex >= 0 ? `${inputId}-option-${focusedIndex}` : undefined"
        data-1p-ignore
        autocomplete="off"
        @input="handleInput"
        @focus="handleFocus"
        @keydown="handleKeyDown"
      >

      <!-- Create button -->
      <AM3Button
        v-if="showCreateButton"
        type="secondary"
        size="xs"
        icon-left="plus"
        text="Create"
        aria-label="Create new option"
        :class="[
          'px-2 py-1 absolute top-1/2 -translate-y-1/2 cursor-pointer flex items-center gap-1 text-xs text-brand hover:text-brand-dark font-medium transition-colors bg-surface-light hover:bg-brand-light rounded',
          createButtonPositionClass,
        ]"
        @click.stop="createNewOption"
        @mousedown.prevent
      />

      <!-- Clear button -->
      <button
        v-if="showClearButton"
        aria-label="Clear selection"
        :data-test-id="generateDataTestId(dataTestId, 'clear')"
        :class="[
          'size-6 hover:bg-brand-light transform absolute top-1/2 -translate-y-1/2 cursor-pointer flex flex-row items-center justify-center rounded-full outline-transparent outline outline-1 focus:outline-brand',
          clearButtonPositionClass,
        ]"
        @click="clearSelection"
        @mousedown.prevent
      >
        <AM3Icon
          type="x-mark"
          size="sm"
        />
      </button>

      <!-- Chevron icon / Loading indicator -->
      <div
        class="absolute flex flex-col items-center justify-center h-full transform pointer-events-none am3-input-slot right-3"
      >
        <div
          v-if="isLoading"
          class="border-2 rounded-full animate-spin size-4 border-brand border-t-transparent"
        />
        <AM3Icon
          v-else
          type="chevron-down"
          size="sm"
          class="transition-transform transform duration-250"
          :class="
            isOpen ? 'rotate-180 translate-y-[-1px]' : 'rotate-0 translate-y-0'
          "
        />
      </div>

      <!-- Right slot -->
      <div
        v-if="$slots.right"
        class="flex items-center am3-input-slot right"
      >
        <slot name="right" />
      </div>
    </div>

    <AM3InputError :error="validationState" />

    <!-- Dropdown panel with options -->
    <AM3DropdownPanel
      ref="dropdownPanelRef"
      v-model="isOpen"
      :target="inputId"
      stretch
      :full-height="stretchDropdown"
    >
      <div
        role="listbox"
        class="w-full overflow-y-auto am3-select-options"
        :class="stretchDropdown ? 'max-h-[calc(100vh-16rem)]' : 'max-h-[360px]'"
        @wheel.prevent="handleScroll"
        @touchmove.prevent="handleTouchScroll"
        @scroll="handleNativeScroll"
      >
        <!-- Group options by category -->
        <template
          v-for="(group, categoryName) in groupedOptions"
          :key="categoryName"
        >
          <!-- Category header if category exists -->
          <div
            v-if="categoryName"
            class="am3-select-category"
          >
            <div class="font-bold am3-category-content">
              <slot
                name="category"
                :category="categoryName"
              >
                <span>{{ categoryName }}</span>
              </slot>
            </div>
          </div>

          <!-- Options in this category -->
          <div
            v-for="(option, index) in group"
            :id="`${inputId}-option-${getOptionFlatIndex(option)}`"
            :key="getOptionKey(option, index)"
            role="option"
            :aria-selected="isOptionSelected(option)"
            :class="[
              'am3-select-option font-light flex justify-between items-center',
              isOptionSelected(option) ? 'selected' : '',
              isOptionFocused(option) && isKeyboardNavigating ? 'focused' : '',
            ]"
            :data-test-id="
              generateDataTestId(
                dataTestId,
                'input-option',
                typeof getOptionValue(option, index) === 'object'
                  ? index
                  : getOptionValue(option, index),
              )
            "
            @click="onOptionClick(option)"
          >
            <div class="flex items-center flex-grow min-w-0">
              <!-- Selection indicator icon for checkbox style (multiple select) -->
              <div
                v-if="multiple && showSelectionIndicators"
                class="flex items-center justify-center flex-shrink-0 mr-2 transition-colors bg-transparent border border-solid rounded-sm size-4"
                :class="
                  isOptionSelected(option) && multiple
                    ? ' text-brand border-brand'
                    : ' text-primary border-primary'
                "
              >
                <AM3Icon
                  v-if="isOptionSelected(option)"
                  type="check-thick"
                  size="sm"
                  class="text-white"
                />
              </div>

              <!-- Selection indicator icon for radio button style (single select) -->
              <div
                v-else-if="!multiple && showSelectionIndicators"
                class="items-center justify-center hidden w-4 h-4 mr-2 transition-colors border border-solid rounded-full contrast-more:flex text-primary"
                :class="
                  isOptionSelected(option)
                    ? 'bg-surface-light border-brand'
                    : 'bg-transparent border-primary'
                "
              >
                <div
                  v-if="isOptionSelected(option)"
                  class="w-2 h-2 rounded-full bg-brand"
                />
              </div>

              <slot
                name="option"
                :option="option"
              >
                <span class="truncate">{{ getOptionLabel(option) }}</span>
              </slot>
            </div>
          </div>
        </template>

        <slot name="after-options" />

        <div
          v-if="filteredOptions.length === 0"
          class="am3-select-no-results"
        >
          No results found
        </div>
      </div>
    </AM3DropdownPanel>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  type PropType,
  ref,
  watch,
  onMounted,
  onBeforeUnmount,
  nextTick,
  onUpdated,
} from 'vue'
import { v4 as uuidv4 } from 'uuid'
import AM3Button from '@/components/am3-generic/base/AM3Button.vue'
import { generateDataTestId } from '@/components/am3-generic/core/dataTestId'
import '@/components/am3-generic/core/theme.css'
import AM3DropdownPanel from '@/components/am3-generic/base/AM3DropdownPanel.vue'
import AM3Tag from '@/components/am3-generic/base/AM3Tag.vue'
import AM3Icon from '@/components/am3-generic/base/icon/AM3Icon.vue'
import { useFormValidation } from '@/components/am3-generic/inputs/useFormValidation'
import AM3InputError from '@/components/am3-generic/inputs/AM3InputError.vue'
import AM3InputLabel from '@/components/am3-generic/inputs/AM3InputLabel.vue'

/**
 * AM3InputSelect component
 *
 * A select input component with searchable options and custom rendering
 */
// Fetch purposes for standardized event handling
const FETCH_PURPOSES = {
  INITIAL_LOAD: 'initial-load',
  SEARCH: 'search',
  SCROLL_END: 'scroll-end',
  SCROLL_END_BASE: 'scroll-end-base',
  RESTORE_BASE: 'restore-base',
  RECONCILIATION_FAILED: 'reconciliation-failed',
} as const

interface FetchParams {
  searchString?: string;
  searchValue?: any;
  offset?: number;
  limit?: number;
  purpose: string;
}

type FetchFunction = (params: FetchParams) => Promise<any>

export default defineComponent({
  name: 'AM3InputSelect',

  components: {
    AM3Button,
    AM3InputError,
    AM3InputLabel,
    AM3Icon,
    AM3DropdownPanel,
    AM3Tag,
  },

  props: {
    // v-model value
    modelValue: {
      type: [String, Number, Object, Array],
      default: null,
    },

    // Input label (string or false to hide)
    label: {
      type: [String, Boolean] as PropType<string | false>,
      default: '',
    },

    // Placeholder text
    placeholder: {
      type: String,
      default: props => 'Select ' + props.label.toLowerCase(),
    },

    // Disabled state
    disabled: {
      type: Boolean,
      default: false,
    },

    // Required field
    required: {
      type: Boolean,
      default: false,
    },

    // Options array
    options: {
      type: Array as PropType<any[]>,
      default: () => [],
    },

    // Option label key or function
    optionLabel: {
      type: [String, Function] as PropType<string | ((option: any) => string)>,
      default: 'label',
    },

    // Option value key or function
    optionValue: {
      type: [String, Function] as PropType<string | ((option: any) => any)>,
      default: 'value',
    },

    // Multiple selection
    multiple: {
      type: Boolean,
      default: false,
    },

    // Expands the box upon multiple selection
    expands: {
      type: Boolean,
      default: false,
    },

    // Validation function
    validate: {
      type: Function as PropType<(value: any) => boolean | string>,
      default: () => true,
    },

    // Validate on blur
    validateOnBlur: {
      type: Boolean,
      default: false,
    },

    // Allow creating new options
    allowCreate: {
      type: Boolean,
      default: false,
    },

    // If allowCreate, fire the create event instead of adding to select
    enableCreateEvent: {
      type: Boolean,
      default: false,
    },

    // Data test id prefix
    dataTestId: {
      type: String,
      default: props => props.label,
    },

    // Close dropdown on option selection (even in multiple mode)
    closeOnClick: {
      type: Boolean,
      default: false,
    },

    // Stretch dropdown to maximum height
    stretchDropdown: {
      type: Boolean,
      default: false,
    },

    // Show selection indicators (checkbox/radio)
    showSelectionIndicators: {
      type: Boolean,
      default: true,
    },

    // Page size for pagination
    pageSize: {
      type: Number,
      default: 50,
    },

    // Maximum number of non-selected dynamic options to retain.
    // Selected options are always preserved in addition to this cap.
    maxDisplayedOptions: {
      type: Number,
      default: 1000,
    },

    // Async function prop for fetching options
    fetchOptions: {
      type: Function as PropType<FetchFunction>,
      default: null,
    },

    // Show clear button on hover
    clearable: {
      type: Boolean,
      default: false,
    },

    // Sort selected items to the top
    sortSelectedToTop: {
      type: Boolean,
      default: false,
    },
  },

  emits: [
    'update:modelValue',
    'change',
    'search',
    'focus',
    'blur',
    'create',
    'fetch-options',
  ],

  setup(props, { emit }) {
    // Generate unique ID for the input
    const inputId = ref(`am3-select-${uuidv4()}`)
    const inputRef = ref<HTMLInputElement | null>(null)
    const dropdownPanelRef = ref<InstanceType<typeof AM3DropdownPanel> | null>(null)
    const tagsContainerRef = ref<HTMLElement | null>(null)
    const tagRefs = ref<HTMLElement[]>([])
    const isOpen = ref(false)
    const searchText = ref('')
    const focusedIndex = ref(-1)
    // Distinguishes arrow-key navigation from a mouse click also setting
    // focusedIndex (see onOptionClick), so the focus outline only shows for
    // keyboard use, not as a residual highlight after clicking an option.
    const isKeyboardNavigating = ref(false)

    // Track pagination state
    const currentPage = ref(0) // Start from 0 for skip calculation
    const isLoadingMore = ref(false) // Prevent duplicate scroll-end events

    // Loading state for fetchOptions function
    const isLoading = ref(false)

    // Dynamic options for fetchOptions prop - component manages these internally.
    // The list is capped to prevent unbounded growth, while selected options are
    // always preserved even if they would otherwise be pruned.
    const dynamicOptions = ref<any[]>([])

    // When a server search is active, holds the set of option values returned by the
    // most recent search result. filteredOptions uses this to show only matching items
    // in the dropdown while the full set remains cached in dynamicOptions.
    // null means no search is active — show all accumulated options.
    const activeSearchValues = ref<Set<any> | null>(null)

    // Track if more data is available for fetchOptions
    const hasMoreData = ref(true)

    // Cache of full option objects for all currently selected values.
    // This persists across multiple searches so that previously-selected options
    // (which may no longer be in dynamicOptions after a second search) can still
    // be re-injected when restoring base options.
    const knownSelectedOptions = ref(new Map<any, any>())

    const lastSearchString = ref('')
    const baseCurrentPage = ref(0) // Track pagination for base options separately

    // Setup form validation
    const {
      validationState,
      runValidation: _runValidation,
      resetValidation,
      validateOnBlur,
      handleBlur: _handleBlur,
    } = useFormValidation(props.validate, props.modelValue)

    // Sync the validateOnBlur prop with the composable ref
    validateOnBlur.value = props.validateOnBlur

    // Public method to run validation
    const runValidation = (
      value = props.modelValue,
      checkRequired = props.required,
    ) => {
      return _runValidation(value, checkRequired)
    }

    // Helper function to check if more data is available based on fetch results
    const updateHasMoreData = (
      fetchedResults: any[],
      expectedLimit: number,
    ) => {
      hasMoreData.value = fetchedResults.length >= expectedLimit
    }

    const getSelectedOptionValues = (): any[] => {
      if (props.multiple && Array.isArray(props.modelValue)) {
        return props.modelValue.map(value => getOptionValue(value))
      }

      if (props.modelValue === null || props.modelValue === undefined) {
        return []
      }

      return [getOptionValue(props.modelValue)]
    }

    const capDynamicOptions = (options: any[]): any[] => {
      const selectedValues = getSelectedOptionValues()
      const selectedValueSet = new Set(selectedValues)
      const optionValues = new Set(options.map(opt => getOptionValue(opt)))
      const missingSelectedOptions = selectedValues
        .filter(value => !optionValues.has(value))
        .map(value => knownSelectedOptions.value.get(value))
        .filter(Boolean)

      const optionsWithSelected
        = missingSelectedOptions.length > 0
          ? [...options, ...missingSelectedOptions]
          : options
      const maxOptionsToKeep
        = Math.max(props.maxDisplayedOptions, 0) + selectedValueSet.size

      if (optionsWithSelected.length <= maxOptionsToKeep) {
        return optionsWithSelected
      }

      let removableCount = optionsWithSelected.length - maxOptionsToKeep

      return optionsWithSelected.filter((option) => {
        const optionValue = getOptionValue(option)
        if (removableCount > 0 && !selectedValueSet.has(optionValue)) {
          removableCount -= 1
          return false
        }

        return true
      })
    }

    // Helper to merge new options into an existing list, deduplicating by option value.
    // Existing options are kept in place; only options whose value is not already present are appended.
    // The merged result is capped by maxDisplayedOptions, excluding selected options from pruning.
    const mergeIntoOptions = (existing: any[], newItems: any[]): any[] => {
      if (existing.length === 0) {
        return capDynamicOptions(newItems)
      }

      const existingValues = new Set(
        existing.map(opt => getOptionValue(opt)),
      )
      const uniqueNew = newItems.filter(
        opt => !existingValues.has(getOptionValue(opt)),
      )
      const mergedOptions
        = uniqueNew.length > 0 ? [...existing, ...uniqueNew] : existing

      return capDynamicOptions(mergedOptions)
    }

    // Helper function to restore base options after search
    const restoreBaseOptions = () => {
      // Reset pagination state for fresh restore
      baseCurrentPage.value = 0
      currentPage.value = 0
      isLoadingMore.value = false
      hasMoreData.value = true
      // No active search — show all accumulated options
      activeSearchValues.value = null

      const fetchParams = {
        searchString: '',
        searchValue: undefined,
        offset: 0,
        limit: props.pageSize,
        purpose: FETCH_PURPOSES.RESTORE_BASE,
      }

      // Emit fetch-options event for restore
      emit('fetch-options', fetchParams)

      // Call fetchOptions function prop if provided
      if (props.fetchOptions) {
        isLoading.value = true
        props
          .fetchOptions(fetchParams)
          .then((result) => {
            // Merge fetched base options into the accumulated list so previously loaded
            // options (including selected ones) are never lost.
            dynamicOptions.value = mergeIntoOptions(
              dynamicOptions.value,
              result || [],
            )
            updateHasMoreData(result || [], props.pageSize)
          })
          .finally(() => {
            isLoading.value = false
          })
      }
    }

    // Public method to clear search cache and reset to base state
    // Useful for external components that need to programmatically reset the search state
    const clearSearchCache = () => {
      lastSearchString.value = ''
      currentPage.value = 0
      baseCurrentPage.value = 0
      isLoadingMore.value = false
    }

    // Public method to explicitly flush all accumulated options and reset fetch state.
    // Call this when you need a completely fresh option list (e.g. after a parent data reset).
    // Not called internally — the component always accumulates options across searches/pages.
    const flushOptions = () => {
      dynamicOptions.value = []
      activeSearchValues.value = null
      lastSearchString.value = ''
      currentPage.value = 0
      baseCurrentPage.value = 0
      isLoadingMore.value = false
      hasMoreData.value = true
    }

    // Add a reactive trigger for container width changes
    const containerWidth = ref(0)

    // Update container width when component mounts or updates
    const updateContainerWidth = () => {
      if (inputRef.value?.parentElement) {
        containerWidth.value = inputRef.value.parentElement.clientWidth
      }
    }

    // Calculate how many tags can be visible in normal mode while keeping input at 100px
    const visibleTagsInNormalMode = computed(() => {
      if (selectedOptions.value.length === 0) {
        return selectedOptions.value
      }

      if (!containerWidth.value) {
        // Fallback to showing first 3 tags if we can't measure
        return selectedOptions.value.slice(0, 3)
      }

      const currentContainerWidth = containerWidth.value
      const minInputWidth = 100
      const chevronWidth = 40
      const leftPadding = 16 // ml-2 and p-1
      const rightPadding = 16 // pr-6 and chevron space
      const plusTagWidth = 45 // estimated width of +X tag

      // Available width for tags (more conservative estimate)
      const availableWidth
        = currentContainerWidth
          - minInputWidth
          - chevronWidth
          - leftPadding
          - rightPadding

      // If we don't need a +X tag (all tags fit), don't reserve space for it
      const availableWidthWithoutPlus = availableWidth + plusTagWidth

      let totalTagWidth = 0
      let visibleCount = 0

      // First pass: see if all tags fit without +X tag
      for (const option of selectedOptions.value) {
        const estimatedTagWidth = getOptionLabel(option).length * 7 + 35 + 4 // more accurate estimate
        totalTagWidth += estimatedTagWidth
      }

      if (totalTagWidth <= availableWidthWithoutPlus) {
        // All tags fit, show them all
        return selectedOptions.value
      }

      // Second pass: find how many fit with +X tag
      totalTagWidth = 0
      for (const option of selectedOptions.value) {
        const estimatedTagWidth = getOptionLabel(option).length * 7 + 35 + 4

        if (totalTagWidth + estimatedTagWidth <= availableWidth) {
          totalTagWidth += estimatedTagWidth
          visibleCount++
        }
        else {
          break
        }
      }

      // Always show at least 1 tag if we have any, but cap at total available
      return selectedOptions.value.slice(
        0,
        Math.max(1, Math.min(visibleCount, selectedOptions.value.length)),
      )
    })

    // Get option label
    const getOptionLabel = (option: any): string => {
      if (!option) {
        return ''
      }
      if (typeof props.optionLabel === 'function') {
        return props.optionLabel(option)
      }
      if (typeof option === 'object' && props.optionLabel in option) {
        return option[props.optionLabel]
      }
      return String(option)
    }

    // Get option value
    const getOptionValue = (option: any): any => {
      if (!option) {
        return null
      }
      if (typeof props.optionValue === 'function') {
        return props.optionValue(option)
      }
      if (typeof option === 'object' && props.optionValue in option) {
        return option[props.optionValue]
      }
      return option
    }

    // Get option key for v-for
    const getOptionKey = (option: any, index: number): string => {
      const value = getOptionValue(option)
      return value !== null && value !== undefined
        ? String(value)
        : `option-${index}`
    }

    // Check if option is selected
    const isOptionSelected = (option: any): boolean => {
      if (props.modelValue === null || option.type === 'category') {
        return false
      }
      const optionValue = getOptionValue(option)
      if (props.multiple && Array.isArray(props.modelValue)) {
        return props.modelValue
          .map(getOptionValue)
          .some(value => value === optionValue)
      }

      // For single select, try different matching strategies
      const modelValueProcessed = getOptionValue(props.modelValue)

      // First try exact value match
      if (modelValueProcessed === optionValue) {
        return true
      }

      // If modelValue is an object, try object equality
      if (typeof props.modelValue === 'object' && typeof option === 'object') {
        try {
          return JSON.stringify(option) === JSON.stringify(props.modelValue)
        }
        catch (e) {
          // Fallback to reference equality if JSON.stringify fails
          return option === props.modelValue
        }
      }

      // Fallback to reference equality
      return option === props.modelValue
    }

    // Computed property to get the correct options source for template usage
    // This ensures all template references use the right options regardless of fetchOptions vs props.options
    const allOptions = computed(() => {
      return props.fetchOptions ? dynamicOptions.value : props.options
    })

    // Filter options based on search text
    const filteredOptions = computed(() => {
      if (props.fetchOptions) {
        // When a search is active, show only the options returned by the last server search.
        // The rest of dynamicOptions stays cached but hidden until the search is cleared.
        if (activeSearchValues.value !== null) {
          return dynamicOptions.value.filter(opt =>
            activeSearchValues.value!.has(getOptionValue(opt)),
          )
        }
        return dynamicOptions.value
      }

      // Otherwise, do client-side filtering for static options
      const trimmedSearch = searchText.value.trim()
      if (!trimmedSearch) {
        return allOptions.value
      }

      const search = trimmedSearch.toLowerCase()
      return allOptions.value.filter((option) => {
        return getOptionLabel(option).toLowerCase().includes(search)
      })
    })

    // Group options by category
    const groupedOptions = computed(() => {
      const groups: Record<string, any[]> = {}

      filteredOptions.value.forEach((option) => {
        const category = option.category || ''
        if (!groups[category]) {
          groups[category] = []
        }
        groups[category].push(option)
      })

      // Sort selected items to top within each category
      if (props.sortSelectedToTop) {
        Object.keys(groups).forEach((category) => {
          groups[category].sort((a, b) => {
            const aSelected = isOptionSelected(a)
            const bSelected = isOptionSelected(b)
            if (aSelected && !bSelected) return -1
            if (!aSelected && bSelected) return 1
            return 0 // Maintain original order for items with same selection state
          })
        })
      }

      return groups
    })

    // Flat ordered list of all rendered options (mirrors the template's v-for order)
    const flatFilteredOptions = computed(() =>
      Object.values(groupedOptions.value).flat(),
    )

    const isOptionFocused = (option: any) => {
      if (focusedIndex.value < 0) return false
      return flatFilteredOptions.value.indexOf(option) === focusedIndex.value
    }

    const getOptionFlatIndex = (option: any) =>
      flatFilteredOptions.value.indexOf(option)

    // Get selected options for multiple selection
    const selectedOptions = computed(() => {
      if (
        !props.multiple
        || !Array.isArray(props.modelValue)
        || props.modelValue.length === 0
      ) {
        return []
      }
      return props.modelValue.map((value) => {
        // If the value is already an option object, return it
        if (typeof value === 'object') {
          return value
        }
        // Otherwise, find the option in the options array
        const optionValue = getOptionValue(value)
        return (
          allOptions.value.find(
            opt => getOptionValue(opt) === optionValue,
          ) || {
            [props.optionLabel as string]: String(value),
            [props.optionValue as string]: value,
          }
        )
      })
    })

    // In expands mode, once at least one tag is selected, size the input to its typed
    // content (like MUI's Autocomplete) instead of stretching via flex-1 -- otherwise it
    // fills whatever's left of the wrapped row, often landing as an oversized empty box
    // on its own line below the tags. Returns undefined outside that case so the input
    // falls back to the regular flex-1 fill behaviour.
    const expandedInputSize = computed(() => {
      if (!props.expands || selectedOptions.value.length === 0) {
        return undefined
      }
      return Math.max(searchText.value.length + 1, 3)
    })

    // Check if clear button should be visible
    const showClearButton = computed(() => {
      if (props.clearable === false || props.disabled) {
        return false
      }

      if (props.multiple && Array.isArray(props.modelValue)) {
        return props.modelValue.length > 0
      }

      return (
        props.modelValue !== null
        && props.modelValue !== undefined
        && props.modelValue !== ''
      )
    })

    const showCreateButton = computed(() => {
      return props.allowCreate && !!searchText.value && !isExactMatch.value
    })

    const createButtonPositionClass = computed(() => {
      if (props.multiple && showClearButton.value) {
        return 'right-[72px]'
      }

      return 'right-10'
    })

    const clearButtonPositionClass = computed(() => {
      if (props.multiple) {
        return 'right-10'
      }

      return showCreateButton.value ? 'right-8' : 'right-10'
    })

    // Check if search text exactly matches an existing option
    const isExactMatch = computed(() => {
      const trimmedSearch = searchText.value.trim()
      if (!trimmedSearch) return false
      const searchLower = trimmedSearch.toLowerCase()
      return allOptions.value.some(
        option => getOptionLabel(option).toLowerCase().trim() === searchLower,
      )
    })

    // Remove an option from the selection
    const removeOption = (option: any) => {
      if (
        props.disabled
        || !props.multiple
        || !Array.isArray(props.modelValue)
      ) {
        return
      }
      const optionValue = getOptionValue(option)
      knownSelectedOptions.value.delete(optionValue)
      const newValue = props.modelValue.filter(
        value => getOptionValue(value) !== optionValue,
      )
      emit('update:modelValue', newValue)
      emit('change', newValue)
    }

    // Select an option
    const selectOption = (option: any) => {
      if (props.disabled || option.type === 'category') {
        return
      }
      const optionValue = getOptionValue(option)

      if (props.multiple && Array.isArray(props.modelValue)) {
        const isSelected = isOptionSelected(option)
        if (isSelected) {
          return removeOption(option)
        }
        // Cache the full option object so restoreBaseOptions can re-inject it
        // even after dynamicOptions has been replaced by a later search
        knownSelectedOptions.value.set(optionValue, option)
        const newValue = [...props.modelValue, optionValue]

        emit('update:modelValue', newValue)
        emit('change', newValue)

        // Clear the search text and un-narrow the list after a pick, so the
        // dropdown isn't left showing only the outgoing search's matches
        // while the box looks empty. activeSearchValues = null makes
        // filteredOptions fall back to the full accumulated dynamicOptions
        // list immediately, with no extra network round trip (unlike
        // restoreBaseOptions, which also re-fetches page 0 - not needed just
        // to un-narrow what's already cached). lastSearchString is cleared
        // too so a still-in-flight response for the old search term gets
        // discarded by the staleness check instead of silently re-narrowing
        // the list right after we just cleared it.
        // Only when fetchOptions is set - for static option lists,
        // filteredOptions derives directly from searchText, so clearing it
        // would change that path's behavior too; leave that as-is.
        if (props.fetchOptions) {
          searchText.value = ''
          lastSearchString.value = ''
          activeSearchValues.value = null
        }

        // Close dropdown if closeOnClick is true, even in multiple mode
        if (props.closeOnClick) {
          isOpen.value = false
        }
      }
      else {
        // Cache the full option object for single select too
        knownSelectedOptions.value.set(optionValue, option)
        emit('update:modelValue', optionValue)
        emit('change', optionValue)

        // Update search text to show selected option
        searchText.value = getOptionLabel(option)

        // Close dropdown after selection (for single select or if closeOnClick is true)
        if (!props.multiple || props.closeOnClick) {
          isOpen.value = false
        }
      }
    }

    // Click handler for option items — captures flat index before state changes,
    // then sets focusedIndex via nextTick so it lands after any watcher resets.
    const onOptionClick = (option: any) => {
      const idx = getOptionFlatIndex(option)
      selectOption(option)
      if (props.multiple && idx >= 0) {
        isKeyboardNavigating.value = false
        nextTick(() => {
          focusedIndex.value = idx
        })
      }
    }

    // Create a new option when clicking the "Create new" button
    const createNewOption = () => {
      // IMPORTANT: Capture searchText immediately before anything else
      // because clicking the button might trigger toggleDropdown which clears it
      const capturedSearchText = searchText.value

      if (!capturedSearchText || isExactMatch.value) {
        return
      }

      const newItemLabel = capturedSearchText.trim()

      // Emit create event for parent to handle
      // Parent should create via API, then manually select the created item
      emit('create', newItemLabel)

      // Clear search text after emitting event
      searchText.value = ''

      // Keep dropdown open for multiple selection
      if (!props.multiple) {
        isOpen.value = false
      }
    }

    // Clear selection and search
    const clearSelection = (event: Event) => {
      event.stopPropagation() // Prevent dropdown toggle

      // Clear the selection
      knownSelectedOptions.value.clear()
      if (props.multiple) {
        emit('update:modelValue', [])
        emit('change', [])
      }
      else {
        emit('update:modelValue', null)
        emit('change', null)
      }

      // Clear search text
      searchText.value = ''

      // Clear search state and restore base options if we were searching
      if (lastSearchString.value) {
        lastSearchString.value = ''
        restoreBaseOptions()
      }

      // Reset validation state
      resetValidation()
    }

    // Toggle dropdown
    const toggleDropdown = () => {
      if (props.disabled) return

      const wasOpen = isOpen.value
      isOpen.value = !isOpen.value

      // Reset search text when opening or closing dropdown
      if (!props.multiple) {
        // For single select, show the selected option when closed, clear when opened
        if (isOpen.value) {
          // Opening dropdown - clear search to allow fresh search
          searchText.value = ''
        }
        else {
          // Closing dropdown - show selected option
          initializeSearchText()
        }
      }
      else {
        // For multiple select, always clear search text
        searchText.value = ''
      }

      // Reset search state when toggling
      if (lastSearchString.value) {
        lastSearchString.value = ''
        if (!isOpen.value) {
          // Restore base options when closing
          restoreBaseOptions()
        }
      }

      if (isOpen.value && inputRef.value) {
        inputRef.value.focus()
      }
      else {
        inputRef.value.blur()
      }
    }

    // Handle input changes
    const SEARCH_DEBOUNCE_MS = 250
    let searchDebounceTimer: ReturnType<typeof setTimeout> | undefined
    const handleInput = (event: Event) => {
      const target = event.target as HTMLInputElement
      searchText.value = target.value

      // Open dropdown when typing
      if (!isOpen.value) {
        isOpen.value = true
      }

      emit('search', searchText.value)

      // Handle fetch-options when searching or restoring
      if (searchText.value.trim()) {
        // Track base pagination state before first search
        if (!lastSearchString.value) {
          baseCurrentPage.value = currentPage.value
        }

        // Reset pagination for new search
        currentPage.value = 0
        isLoadingMore.value = false
        hasMoreData.value = true // Reset hasMore for new search
        lastSearchString.value = searchText.value.trim()

        const fetchParams = {
          searchString: searchText.value.trim(),
          searchValue: undefined,
          offset: 0,
          limit: props.pageSize,
          purpose: FETCH_PURPOSES.SEARCH,
        }

        // Emit fetch-options event
        emit('fetch-options', fetchParams)

        // Call fetchOptions function prop if provided
        if (props.fetchOptions) {
          isLoading.value = true
          const requestedSearch = searchText.value.trim()
          const fetchOptions = props.fetchOptions
          // Debounced: only the last keystroke in a burst issues a request
          clearTimeout(searchDebounceTimer)
          searchDebounceTimer = setTimeout(() => {
            fetchOptions(fetchParams)
              .then((result) => {
                const resultArray = result || []
                // Merge search results into the full cache — existing options are never lost
                dynamicOptions.value = mergeIntoOptions(
                  dynamicOptions.value,
                  resultArray,
                )
                // Ignore stale search responses after the user clears or changes the search.
                if (lastSearchString.value !== requestedSearch) {
                  return
                }
                // Track which values match this search so filteredOptions can show only them
                activeSearchValues.value = new Set(
                  resultArray.map((opt: any) => getOptionValue(opt)),
                )
                updateHasMoreData(resultArray, props.pageSize)
              })
              .finally(() => {
                isLoading.value = false
              })
          }, SEARCH_DEBOUNCE_MS)
        }
      }
      else if (lastSearchString.value) {
        clearTimeout(searchDebounceTimer)
        lastSearchString.value = ''
        // Clear the search filter immediately so all cached options become visible
        activeSearchValues.value = null
        restoreBaseOptions()
      }
    }

    // Handle focus
    const handleFocus = () => {
      if (props.disabled) return

      emit('focus')

      // Store the current display value before potentially clearing
      const previousSearchText = searchText.value

      // No need to trigger restore here since we're preserving active searches

      // When dropdown closes, handle search text and blur
      const stopWatcher = watch(isOpen, (newIsOpen) => {
        if (!newIsOpen) {
          if (props.multiple) {
            // In multiple mode, clear search text and reset search state on blur/dropdown close
            searchText.value = ''
            if (lastSearchString.value) {
              lastSearchString.value = ''
              // Restore base options after clearing search in multiple mode
              restoreBaseOptions()
            }
          }
          else {
            // In single mode, show the selected option label
            let selectedOption = null

            // Use the same enhanced matching logic as initializeSearchText
            selectedOption = allOptions.value.find(
              option =>
                getOptionValue(option) === getOptionValue(props.modelValue),
            )

            // If no match and modelValue is an object, try matching the whole object
            if (!selectedOption && typeof props.modelValue === 'object') {
              selectedOption = allOptions.value.find(
                option =>
                  JSON.stringify(option) === JSON.stringify(props.modelValue),
              )
            }

            // If still no match, try matching by reference
            if (!selectedOption) {
              selectedOption = allOptions.value.find(
                option => option === props.modelValue,
              )
            }

            if (selectedOption) {
              searchText.value = getOptionLabel(selectedOption)
            }
            else if (props.modelValue !== null) {
              if (
                typeof props.modelValue === 'object'
                && typeof props.optionLabel === 'string'
                && props.optionLabel in props.modelValue
              ) {
                searchText.value = props.modelValue[props.optionLabel]
              }
              else {
                searchText.value
                  = previousSearchText || String(props.modelValue)
              }
            }
            else {
              // No selection, clear search text
              searchText.value = ''
            }

            // Reset search state on blur in single mode too
            if (lastSearchString.value) {
              lastSearchString.value = ''
              restoreBaseOptions()
            }
          }

          // Handle blur validation when dropdown closes
          _handleBlur(props.modelValue)
          emit('blur')

          // Remove the watcher after it's used
          stopWatcher()
        }
      })
    }

    // Handle keydown events
    const handleKeyDown = (event: KeyboardEvent) => {
      if (props.disabled) return

      // Close dropdown on escape
      if (event.key === 'Escape') {
        isOpen.value = false
        inputRef.value?.blur()
        return
      }

      // Close dropdown on tab (allow natural focus movement)
      if (event.key === 'Tab') {
        isOpen.value = false
        return
      }

      // Arrow down: open or navigate down
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        isKeyboardNavigating.value = true
        if (!isOpen.value) {
          isOpen.value = true
          const currentIndex = flatFilteredOptions.value.findIndex(opt => isOptionSelected(opt))
          focusedIndex.value = currentIndex >= 0 ? currentIndex : 0
        }
        else {
          focusedIndex.value = focusedIndex.value >= flatFilteredOptions.value.length - 1
            ? 0
            : focusedIndex.value + 1
        }
        nextTick(() => {
          document.getElementById(`${inputId.value}-option-${focusedIndex.value}`)?.scrollIntoView({ block: 'nearest' })
        })
        return
      }

      // Arrow up: navigate up, wrapping from the first option to the last
      // (matches MUI Autocomplete's changeHighlightedIndex wrap-around)
      if (event.key === 'ArrowUp') {
        event.preventDefault()
        if (isOpen.value) {
          isKeyboardNavigating.value = true
          focusedIndex.value = focusedIndex.value <= 0
            ? flatFilteredOptions.value.length - 1
            : focusedIndex.value - 1
          nextTick(() => {
            document.getElementById(`${inputId.value}-option-${focusedIndex.value}`)?.scrollIntoView({ block: 'nearest' })
          })
        }
        return
      }

      // Backspace on an empty input removes the most recently added tag
      // (matches MUI Autocomplete's multiple-value Backspace behavior)
      if (
        event.key === 'Backspace'
        && props.multiple
        && !searchText.value
        && selectedOptions.value.length > 0
      ) {
        removeOption(selectedOptions.value[selectedOptions.value.length - 1])
        return
      }

      // Enter: select focused option, or create new if allowCreate
      if (event.key === 'Enter') {
        if (isOpen.value && focusedIndex.value >= 0) {
          event.preventDefault()
          const option = flatFilteredOptions.value[focusedIndex.value]
          if (option) {
            selectOption(option)
          }
          return
        }

        const normalizedSearch = searchText.value.trim().toLowerCase()
        const exactMatch = normalizedSearch
          ? flatFilteredOptions.value.find(
              option => getOptionLabel(option).trim().toLowerCase() === normalizedSearch,
            )
          : undefined

        if (isOpen.value && exactMatch) {
          event.preventDefault()
          selectOption(exactMatch)
          return
        }

        if (isOpen.value && flatFilteredOptions.value.length === 1) {
          event.preventDefault()
          selectOption(flatFilteredOptions.value[0])
          return
        }

        if (props.allowCreate && searchText.value) {
          const exists = allOptions.value.some(
            option =>
              getOptionLabel(option).toLowerCase()
              === searchText.value.toLowerCase(),
          )
          if (!exists) {
            if (props.enableCreateEvent) {
              emit('create', searchText.value)
            }
            else {
              const newOption
                = typeof allOptions.value[0] === 'object'
                  ? {
                      [props.optionLabel as string]: searchText.value,
                      [props.optionValue as string]: searchText.value,
                    }
                  : searchText.value
              if (props.multiple && Array.isArray(props.modelValue)) {
                const newValue = [...props.modelValue, getOptionValue(newOption)]
                emit('update:modelValue', newValue)
                emit('change', newValue)
              }
              else {
                selectOption(newOption)
              }
            }
            searchText.value = ''
            if (!props.multiple) {
              isOpen.value = false
            }
          }
        }
      }
    }

    const fetchMoreOptions = () => {
      if (isLoadingMore.value) {
        return
      }
      if (props.fetchOptions && !hasMoreData.value) {
        return
      }

      isLoadingMore.value = true

      const trimmedSearch = searchText.value.trim()
      if (trimmedSearch) {
        // For search pagination
        currentPage.value += 1
      }
      else {
        // For base options pagination
        baseCurrentPage.value += 1
        currentPage.value = baseCurrentPage.value
      }

      const fetchParams = {
        searchString: trimmedSearch,
        searchValue: undefined,
        offset: currentPage.value * props.pageSize,
        limit: props.pageSize,
        purpose: trimmedSearch
          ? FETCH_PURPOSES.SCROLL_END
          : FETCH_PURPOSES.SCROLL_END_BASE,
      }

      // Emit fetch-options event
      emit('fetch-options', fetchParams)

      // Call fetchOptions function prop if provided
      if (props.fetchOptions) {
        isLoading.value = true
        props
          .fetchOptions(fetchParams)
          .then((result) => {
            const resultArray = result || []
            if (resultArray.length > 0) {
              dynamicOptions.value = mergeIntoOptions(
                dynamicOptions.value,
                resultArray,
              )
              // If paginating within a search, extend the visible set with the new page's values
              if (activeSearchValues.value !== null) {
                const extended = new Set(activeSearchValues.value)
                resultArray.forEach((opt: any) =>
                  extended.add(getOptionValue(opt)),
                )
                activeSearchValues.value = extended
              }
            }
            updateHasMoreData(resultArray, props.pageSize)
          })
          .catch((error) => {
            console.error(
              '[AM3InputSelect] Error fetching more options:',
              error,
            )
          })
          .finally(() => {
            isLoading.value = false
            // Reset loading flag after fetch completes with a small delay
            setTimeout(() => {
              isLoadingMore.value = false
            }, 100)
          })
      }
      else {
        // Reset loading flag if no fetchOptions provided
        setTimeout(() => {
          isLoadingMore.value = false
        }, 100)
      }
    }

    const handleScroll = (event: WheelEvent) => {
      const container = event.currentTarget as HTMLElement

      // Calculate if scroll should be allowed
      const { scrollTop, scrollHeight, clientHeight } = container
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 50
      if (
        (scrollTop <= 0 && event.deltaY < 0)
        || (scrollTop + clientHeight >= scrollHeight && event.deltaY > 0)
      ) {
        // At the top and scrolling up, or at the bottom and scrolling down
        // Let the event be prevented (already done with .prevent)
      }
      else {
        // Otherwise, manually scroll the container
        container.scrollTop += event.deltaY
      }

      // Handle fetch-options when reaching the bottom (for wheel events)
      if (isAtBottom && event.deltaY > 0) {
        fetchMoreOptions()
      }

      // Prevent the event from closing the dropdown
      event.stopPropagation()
    }

    const handleTouchScroll = (event: TouchEvent) => {
      // Prevent default to stop parent scrolling
      event.stopPropagation()
    }

    const handleNativeScroll = (event: Event) => {
      const container = event.target as HTMLElement
      const { scrollTop, scrollHeight, clientHeight } = container

      // Check if we're at the bottom with a larger threshold for better UX
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 50

      // Handle fetch-options when reaching the bottom (for native scroll events)
      if (isAtBottom) {
        fetchMoreOptions()
      }
    }

    const initializeSearchText = () => {
      if (
        props.modelValue === null
        || (props.multiple
          && Array.isArray(props.modelValue)
          && props.modelValue.length === 0)
      ) {
        searchText.value = ''
        return
      }

      if (!props.multiple) {
        // For single select, try different matching strategies
        let selectedOption = null

        // First, try exact match with getOptionValue
        selectedOption = allOptions.value.find(
          option =>
            getOptionValue(option) === getOptionValue(props.modelValue),
        )

        // If no match and modelValue is an object, try matching the whole object
        if (!selectedOption && typeof props.modelValue === 'object') {
          selectedOption = allOptions.value.find(
            option =>
              JSON.stringify(option) === JSON.stringify(props.modelValue),
          )
        }

        // If still no match, try matching by reference
        if (!selectedOption) {
          selectedOption = allOptions.value.find(
            option => option === props.modelValue,
          )
        }

        if (selectedOption) {
          searchText.value = getOptionLabel(selectedOption)
        }
        else if (
          typeof props.modelValue === 'object'
          && typeof props.optionLabel === 'string'
          && props.optionLabel in props.modelValue
        ) {
          searchText.value = props.modelValue[props.optionLabel]
        }
        else {
          searchText.value = String(props.modelValue)
        }
      }
    }

    // Call initialization on component creation
    nextTick(() => {
      initializeSearchText()
    })

    // Reconcile value with available options when options change
    const reconcileValueWithOptions = () => {
      if (props.modelValue === null || allOptions.value.length === 0) {
        return
      }

      let reconciliationFailed = false

      if (props.multiple && Array.isArray(props.modelValue)) {
        // For multiple selection, filter out values that don't exist in options
        const validValues = props.modelValue.filter((value) => {
          const valueToCheck = getOptionValue(value)
          return allOptions.value.some(
            option => getOptionValue(option) === valueToCheck,
          )
        })

        // Only emit if there are changes
        if (validValues.length !== props.modelValue.length) {
          if (!props.fetchOptions) {
            emit('update:modelValue', validValues)
            emit('change', validValues)
          }
          reconciliationFailed = true
        }
      }
      else {
        // For single selection, check if current value exists in options
        const currentValue = getOptionValue(props.modelValue)

        // Use the same matching logic as isOptionSelected for consistency
        const valueExists = allOptions.value.some((option) => {
          const optionValue = getOptionValue(option)

          // Try exact value match
          if (currentValue === optionValue) {
            return true
          }

          // If both are objects, try JSON comparison
          if (
            typeof currentValue === 'object'
            && typeof optionValue === 'object'
          ) {
            try {
              return (
                JSON.stringify(currentValue) === JSON.stringify(optionValue)
              )
            }
            catch (e) {
              return currentValue === optionValue
            }
          }

          return false
        })

        if (!valueExists) {
          if (!props.fetchOptions) {
            emit('update:modelValue', null)
            emit('change', null)
          }
          reconciliationFailed = true
        }
      }

      // Handle fetch-options when reconciliation fails
      if (reconciliationFailed) {
        const fetchParams = {
          searchString: undefined,
          searchValue: props.modelValue,
          offset: undefined,
          limit: undefined,
          purpose: FETCH_PURPOSES.RECONCILIATION_FAILED,
        }

        // Emit fetch-options event
        emit('fetch-options', fetchParams)

        // Call fetchOptions function prop if provided
        if (props.fetchOptions) {
          isLoading.value = true
          props
            .fetchOptions(fetchParams)
            .then((result) => {
              // Reuse the same merge/cap logic as the other fetch flows.
              dynamicOptions.value = mergeIntoOptions(
                dynamicOptions.value,
                result || [],
              )
              // Reset hasMore for reconciliation as we might have more data available
              hasMoreData.value = true
            })
            .finally(() => {
              isLoading.value = false
            })
        }
      }
    }

    // Watch for changes to modelValue or options to update searchText
    watch(
      [() => props.modelValue, () => allOptions.value],
      ([newModelValue, newOptions], [oldModelValue, oldOptions]) => {
        // Only initialize search text if user is NOT actively searching
        // This prevents clearing search text when options are repopulated during search
        if (!lastSearchString.value) {
          initializeSearchText()
        }

        // Opportunistically populate knownSelectedOptions from the current options list.
        // This covers pre-populated modelValues where selectOption was never called
        // (e.g., component initialized with an existing selection loaded from the store).
        if (newModelValue && newOptions && newOptions.length > 0) {
          if (props.multiple && Array.isArray(newModelValue)) {
            for (const v of newModelValue) {
              const val = getOptionValue(v)
              if (!knownSelectedOptions.value.has(val)) {
                const found = newOptions.find(
                  (opt: any) => getOptionValue(opt) === val,
                )
                if (found) knownSelectedOptions.value.set(val, found)
              }
            }
          }
          else if (newModelValue !== null) {
            const val = getOptionValue(newModelValue)
            if (!knownSelectedOptions.value.has(val)) {
              const found = newOptions.find(
                (opt: any) => getOptionValue(opt) === val,
              )
              if (found) knownSelectedOptions.value.set(val, found)
            }
          }
        }

        // Reconcile when options change
        const optionsChanged = newOptions !== oldOptions
        // Also reconcile when modelValue changes and we now have options (handles async loading scenarios)
        const modelValueChangedWithOptions
          = newModelValue !== oldModelValue
            && newOptions
            && newOptions.length > 0

        // Do NOT reconcile while the user is actively searching: search results are a filtered
        // subset of all options and must never be used to clear a valid selection.
        if (
          !lastSearchString.value
          && (optionsChanged || modelValueChangedWithOptions)
        ) {
          reconcileValueWithOptions()
        }

        if (props.fetchOptions) {
          dynamicOptions.value = capDynamicOptions(dynamicOptions.value)
        }
      },
    )

    // Watch for container width changes
    watch(containerWidth, () => {
      // Force recalculation of visible tags
    })

    // Handle form validation events that prevent click bubbling
    const handleFormValidationClick = (event: MouseEvent) => {
      // Check if this click is on a button that's likely a form submit button
      const target = event.target as HTMLElement
      const button = target.closest('button')

      if (button && isOpen.value) {
        // IMPORTANT: Don't close dropdown for the component's own "Create new" button
        const isOwnCreateButton
          = button.getAttribute('aria-label') === 'Create new option'

        if (isOwnCreateButton) {
          return // Don't close dropdown for our own create button
        }

        // Look for common submit button patterns
        const buttonText = button.textContent?.toLowerCase() || ''
        const isLikelySubmitButton
          = buttonText.includes('apply')
            || buttonText.includes('save')
            || buttonText.includes('submit')
            || buttonText.includes('create')
            || buttonText.includes('update')

        if (isLikelySubmitButton) {
          // Close dropdown when form submission is attempted
          isOpen.value = false
        }
      }
    }

    // Use ResizeObserver to detect container width changes and make initial fetchOptions call
    onMounted(() => {
      updateContainerWidth()
      if (inputRef.value?.parentElement && window.ResizeObserver) {
        const resizeObserver = new ResizeObserver(updateContainerWidth)
        resizeObserver.observe(inputRef.value.parentElement)
      }

      // Initialize search text after mounting to ensure proper display of selected values
      nextTick(() => {
        initializeSearchText()
      })

      // Add event listener for form validation clicks with capture phase
      // This runs before form validation can prevent event propagation
      document.addEventListener('click', handleFormValidationClick, {
        capture: true,
      })

      // Make initial fetchOptions call to populate first page
      if (props.fetchOptions) {
        const fetchParams = {
          searchString: '',
          searchValue: undefined,
          offset: 0,
          limit: props.pageSize,
          purpose: FETCH_PURPOSES.INITIAL_LOAD,
        }

        // Emit fetch-options event for initial load
        emit('fetch-options', fetchParams)

        // Call fetchOptions function prop for initial load
        isLoading.value = true
        props
          .fetchOptions(fetchParams)
          .then((result) => {
            // Merge initial options — consistent with search/paginate behavior
            dynamicOptions.value = mergeIntoOptions(
              dynamicOptions.value,
              result || [],
            )
            updateHasMoreData(result || [], props.pageSize)
          })
          .finally(() => {
            isLoading.value = false
          })
      }
    })

    // Cleanup event listeners
    onBeforeUnmount(() => {
      clearTimeout(searchDebounceTimer)
      document.removeEventListener('click', handleFormValidationClick, {
        capture: true,
      })
    })

    watch(isOpen, (newVal) => {
      if (!newVal) {
        focusedIndex.value = -1
        isKeyboardNavigating.value = false
      }
    })

    watch(searchText, () => {
      focusedIndex.value = -1
    })

    // Options can change size asynchronously for reasons other than typing
    // (a fetchOptions response landing, pagination appending more rows), which
    // the searchText watcher above doesn't cover. Without this, a focusedIndex
    // set by arrow-key wrap-around could point past the end of a list that
    // shrank/was still loading underneath it -- clamp it back into range
    // whenever the list itself changes.
    watch(flatFilteredOptions, (newList) => {
      if (focusedIndex.value >= newList.length) {
        focusedIndex.value = newList.length - 1
      }
    })

    watch(filteredOptions, () => {
      if (isOpen.value) {
        nextTick(() => {
          dropdownPanelRef.value?.updatePosition()
        })
      }
    })

    watch(isLoading, () => {
      if (isOpen.value) {
        nextTick(() => {
          dropdownPanelRef.value?.updatePosition()
        })
      }
    })

    watch(() => props.modelValue, () => {
      if (isOpen.value) {
        nextTick(() => {
          dropdownPanelRef.value?.updatePosition()
        })
      }
    })

    return {
      inputId,
      inputRef,
      dropdownPanelRef,
      tagsContainerRef,
      tagRefs,
      isOpen,
      searchText,
      validationState,
      filteredOptions,
      getOptionLabel,
      getOptionValue,
      getOptionKey,
      isOptionSelected,
      selectedOptions,
      showClearButton,
      showCreateButton,
      removeOption,
      selectOption,
      clearSelection,
      toggleDropdown,
      handleInput,
      handleFocus,
      handleKeyDown,
      generateDataTestId,
      handleScroll,
      handleTouchScroll,
      handleNativeScroll,
      groupedOptions,
      visibleTagsInNormalMode,
      runValidation,
      resetValidation,
      isLoading,
      clearSearchCache,
      flushOptions,
      isExactMatch,
      createNewOption,
      createButtonPositionClass,
      clearButtonPositionClass,
      focusedIndex,
      isKeyboardNavigating,
      flatFilteredOptions,
      isOptionFocused,
      getOptionFlatIndex,
      onOptionClick,
      expandedInputSize,
    }
  },
})
</script>

<style scoped>
/* These styles are kept for backward compatibility,
   but we should transition to use Tailwind classes as per CLAUDE.md guidelines */
.am3-input-select-container {
  @apply flex flex-col w-full relative;
}

.am3-selected-tags {
  @apply flex items-center gap-1;
}

.am3-selected-tag {
  @apply flex items-center bg-[var(--surfaces-colour-informative)] text-[var(--font-colour-brand)]
         rounded-full py-1 px-2 text-xs gap-1;
}

.am3-tag-more {
  @apply bg-[var(--surfaces-colour-neutral)] text-[var(--font-colour-primary)]
         flex items-center justify-center rounded-full w-10 h-6;
}

.am3-select-options {
  @apply overflow-y-auto w-full;
}

.am3-select-option {
  @apply py-2 px-3 cursor-pointer text-sm transition-colors duration-200;
}

/* the events display does not require padding */
.am3-select-option:has(button.am3-event-option) {
  padding: 0;
}

/* the events display does not require backgrounds - eventually replaced by an events picker */
.am3-select-option:has(button.am3-event-option).selected,
.am3-select-option:has(button.am3-event-option):hover {
  background: transparent;
}

.am3-select-option:hover:not(.am3-select-category) {
  @apply bg-[var(--surfaces-colour-neutral)];
}

.am3-select-option.selected {
  @apply bg-[var(--surfaces-colour-informative)] text-[var(--font-colour-brand)] font-medium;
}

/* Keyboard focus must read clearly even on an already-selected row, where
   brand-light (focus) and surfaces-informative (selected) are both pale
   brand tints and easily confused. An inset outline is a distinct visual
   layer from background-color, so it stays visible either way; this rule
   comes after .selected so its background wins too when both classes apply
   (equal specificity, later source order). */
.am3-select-option.focused {
  @apply bg-brand-light outline outline-2 outline-brand outline-offset-[-2px] relative z-10;
}

.am3-select-category {
  @apply py-2 px-3 cursor-default font-semibold text-[var(--font-colour-primary)];
}

.am3-select-category:not(:first-of-type) {
  @apply mt-4;
}

.am3-category-content {
  @apply flex items-center;
}

.am3-select-no-results {
  @apply p-3 text-[var(--font-colour-secondary)] text-sm text-center;
}
</style>
